import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, X, Trash2, Loader2, CheckCircle2, Send, Ban, DollarSign,
  Lock, FileText, Wallet, AlertCircle,
} from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

const STATUS_STYLES = {
  draft:    { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Draft" },
  approved: { cls: "bg-blue-100 text-blue-800 border-blue-200",   label: "Approved" },
  sent:     { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Sent" },
  paid:     { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Paid" },
  void:     { cls: "bg-rose-100 text-rose-700 border-rose-200 line-through", label: "Void" },
};

const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return JSON.stringify(d);
};

export default function InvoicesTab({ project, canManage, canFinance }) {
  const [invoices, setInvoices] = useState([]);
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/projects/${project.id}/invoices`),
      api.get(`/projects/${project.id}/billing-caps`),
    ]).then(([inv, c]) => { setInvoices(inv); setCaps(c); })
      .catch(() => { setInvoices([]); setCaps(null); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [project.id]);

  const doAction = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const approve = (iid) => {
    if (!window.confirm("Approve this invoice? Once approved it becomes IMMUTABLE — only Void will release the amount.")) return;
    doAction(() => api.post(`/projects/${project.id}/invoices/${iid}/approve`, {}), "Invoice approved and locked");
  };

  const voidIt = (iid) => {
    const reason = window.prompt("Void reason (optional):");
    if (reason === null) return;
    doAction(() => api.post(`/projects/${project.id}/invoices/${iid}/void`, { reason }), "Invoice voided — caps released");
  };

  const markSent = (iid) => doAction(() => api.post(`/projects/${project.id}/invoices/${iid}/mark-sent`, {}), "Marked sent");
  const markPaid = (iid) => doAction(() => api.post(`/projects/${project.id}/invoices/${iid}/mark-paid`, {}), "Marked paid");
  const remove = (iid) => {
    if (!window.confirm("Delete this draft?")) return;
    doAction(() => api.del(`/projects/${project.id}/invoices/${iid}`), "Deleted");
  };

  return (
    <div className="space-y-3">
      {/* Header stats */}
      {caps && (
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Contract" value={money(caps.total_contract)} />
          <StatCard label="Billed to date" value={money(caps.total_billed)} color="text-blue-700" />
          <StatCard label="Remaining" value={money(caps.total_remaining)} color="text-emerald-700" bold />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold text-slate-500">Invoices ({invoices.length})</div>
        {canManage && (
          <button
            data-testid="prj-invoice-new-btn"
            onClick={() => { setEditing(null); setWizardOpen(true); }}
            className="text-xs font-bold px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center gap-1.5"
          ><Plus className="w-3.5 h-3.5" /> New invoice</button>
        )}
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-8"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-12 border-2 border-dashed border-slate-200 rounded-lg">
          No invoices yet. Click &quot;New invoice&quot; to bill the client based on completed work.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-2">Number</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Due</div>
            <div className="col-span-2">Line items</div>
            <div className="col-span-2 text-right">Total</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {invoices.map((inv) => (
            <div key={inv.id} data-testid={`prj-invoice-row-${inv.id}`}
              className={`grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50 ${inv.status === "void" ? "opacity-60" : ""}`}
            >
              <div className="col-span-2 font-mono text-[11px] font-semibold">
                <button
                  data-testid={`prj-invoice-open-${inv.id}`}
                  onClick={() => { setEditing(inv); setWizardOpen(true); }}
                  className="hover:underline text-slate-900"
                >{inv.number}</button>
              </div>
              <div className="col-span-2 tabular-nums">{inv.invoice_date}</div>
              <div className="col-span-2 tabular-nums text-slate-500">{inv.due_date || "—"}</div>
              <div className="col-span-2 text-slate-600 truncate">
                {(inv.line_items || []).map((li) => `${li.description} (${li.this_period_pct}%)`).join(", ") || "—"}
              </div>
              <div className="col-span-2 text-right font-semibold tabular-nums">{money(inv.total)}</div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_STYLES[inv.status]?.cls || STATUS_STYLES.draft.cls}`}>
                  {STATUS_STYLES[inv.status]?.label || inv.status}
                </span>
              </div>
              <div className="col-span-1 text-right flex items-center justify-end gap-1">
                {inv.status === "draft" && canFinance && (
                  <>
                    <button data-testid={`prj-invoice-approve-${inv.id}`} onClick={() => approve(inv.id)}
                      className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Approve (locks the invoice)"><CheckCircle2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(inv.id)} className="p-1 rounded hover:bg-red-50 text-red-600" title="Delete draft"><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
                {inv.status === "approved" && canFinance && (
                  <>
                    <button data-testid={`prj-invoice-send-${inv.id}`} onClick={() => markSent(inv.id)}
                      className="p-1 rounded hover:bg-indigo-50 text-indigo-600" title="Mark sent to client"><Send className="w-3.5 h-3.5" /></button>
                    <button data-testid={`prj-invoice-pay-${inv.id}`} onClick={() => markPaid(inv.id)}
                      className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> Paid</button>
                  </>
                )}
                {inv.status === "sent" && canFinance && (
                  <button data-testid={`prj-invoice-pay-${inv.id}`} onClick={() => markPaid(inv.id)}
                    className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> Paid</button>
                )}
                {inv.status !== "void" && canFinance && (
                  <button data-testid={`prj-invoice-void-${inv.id}`} onClick={() => voidIt(inv.id)}
                    className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Void (release the billed % back)"><Ban className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {wizardOpen && (
        <InvoiceWizard
          project={project}
          editing={editing}
          onClose={() => { setWizardOpen(false); setEditing(null); }}
          onSaved={() => { setWizardOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color = "text-slate-900", bold }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
      <div className={`${bold ? "text-xl font-bold" : "text-lg font-semibold"} ${color} tabular-nums mt-0.5`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InvoiceWizard — line-item picker with per-scope % cap
// ---------------------------------------------------------------------------
function InvoiceWizard({ project, editing, onClose, onSaved }) {
  const isEdit = !!editing && editing.status === "draft";
  const isReadOnly = !!editing && editing.status !== "draft";

  const [caps, setCaps] = useState([]);
  const [wbs, setWbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceDate, setInvoiceDate] = useState(editing?.invoice_date || new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(editing?.due_date || "");
  const [taxPct, setTaxPct] = useState(editing?.tax_pct || 0);
  const [notes, setNotes] = useState(editing?.notes || "");
  // pct per scope_id (string for input control)
  const [pcts, setPcts] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/projects/${project.id}/billing-caps`),
      api.get(`/projects/${project.id}/wbs-status`),
    ])
      .then(([c, w]) => {
        if (!alive) return;
        setCaps(c.rows || []);
        setWbs(w.rows || []);
        // Preload existing line items into pcts
        if (editing) {
          const p = {};
          (editing.line_items || []).forEach((li) => { p[li.scope_id] = String(li.this_period_pct); });
          setPcts(p);
        }
      })
      .catch(() => { setCaps([]); setWbs([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [project.id, editing]);

  const rows = useMemo(() => {
    const wbsMap = Object.fromEntries(wbs.map((w) => [w.scope_id, w]));
    return caps.map((c) => {
      const raw = pcts[c.scope_id];
      const pct = parseFloat(raw) || 0;
      const availPct = isEdit ? c.available_pct + (parseFloat(editing.line_items.find((l) => l.scope_id === c.scope_id)?.this_period_pct || 0)) : c.available_pct;
      const amt = c.contract_amount * pct / 100;
      const w = wbsMap[c.scope_id] || {};
      return {
        ...c,
        availableEditPct: availPct,
        pct,
        amt,
        exceedsCap: pct > availPct + 0.01,
        progress_pct: w.progress_pct || 0,
        progress_amount: w.progress_amount || 0,
        steps_total: w.steps_total || 0,
        steps_done: w.steps_done || 0,
        // Suggested "this period %" = executed progress not yet billed
        suggested_pct: Math.max(0, Math.min(availPct, (w.progress_pct || 0) - c.prev_billed_pct)),
      };
    });
  }, [caps, wbs, pcts, editing, isEdit]);

  const subtotal = useMemo(() => rows.reduce((s, r) => s + r.amt, 0), [rows]);
  const taxAmount = useMemo(() => subtotal * (parseFloat(taxPct) || 0) / 100, [subtotal, taxPct]);
  const total = subtotal + taxAmount;
  const anyOver = rows.some((r) => r.exceedsCap);

  const buildPayload = () => ({
    invoice_date: invoiceDate,
    due_date: dueDate || null,
    tax_pct: parseFloat(taxPct) || 0,
    notes,
    line_items: rows.filter((r) => r.pct > 0).map((r) => ({
      scope_id: r.scope_id,
      this_period_pct: r.pct,
    })),
  });

  const save = async () => {
    const payload = buildPayload();
    if (payload.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/projects/${project.id}/invoices/${editing.id}`, payload);
      } else {
        await api.post(`/projects/${project.id}/invoices`, payload);
      }
      toast.success("Invoice saved as draft");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed to save")); }
    finally { setBusy(false); }
  };

  const saveAndApprove = async () => {
    if (anyOver) { toast.error("Fix items exceeding cap before approving"); return; }
    setBusy(true);
    try {
      let iid = editing?.id;
      if (!isEdit) {
        const inv = await api.post(`/projects/${project.id}/invoices`, buildPayload());
        iid = inv.id;
      } else {
        await api.patch(`/projects/${project.id}/invoices/${editing.id}`, buildPayload());
      }
      await api.post(`/projects/${project.id}/invoices/${iid}/approve`, {});
      toast.success("Invoice approved and locked");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Failed to approve")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-invoice-wizard">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2">
              {isReadOnly ? <Lock className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              {editing ? `Invoice ${editing.number}` : "New invoice"}
            </h2>
            {isReadOnly && <div className="text-[11px] text-rose-600 font-semibold mt-0.5">Locked ({editing.status}) — read-only. Use Void to release the billed amount.</div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Invoice date</label>
              <input data-testid="prj-invoice-date"
                type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} disabled={isReadOnly} className={inputCls} /></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Due date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={isReadOnly} className={inputCls} /></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Tax %</label>
              <input type="number" step="0.01" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} disabled={isReadOnly} className={`${inputCls} text-right tabular-nums`} /></div>
          </div>

          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Line items (per scope)</div>
            {loading ? (
              <div className="text-center text-xs text-slate-400 py-6"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
            ) : rows.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 border border-dashed rounded text-center">This project has no scopes yet — add scopes in the WBS tab first.</div>
            ) : (
              <div className="border border-slate-200 rounded overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
                  <div className="col-span-3">Scope</div>
                  <div className="col-span-1 text-right">Contract</div>
                  <div className="col-span-2 text-right">Progress (exec)</div>
                  <div className="col-span-1 text-right">Billed</div>
                  <div className="col-span-1 text-right">Remain 100%</div>
                  <div className="col-span-2 text-right">This period %</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {rows.map((r) => (
                  <div key={r.scope_id} className={`grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-100 text-xs ${r.exceedsCap ? "bg-rose-50" : ""}`} data-testid={`prj-invoice-line-${r.scope_id}`}>
                    <div className="col-span-3 min-w-0">
                      <div className="truncate font-medium text-slate-800">{r.name}</div>
                      {r.steps_total > 0 && (
                        <div className="text-[10px] text-slate-400 tabular-nums">{r.steps_done}/{r.steps_total} items received / done</div>
                      )}
                    </div>
                    <div className="col-span-1 text-right tabular-nums font-semibold">{money(r.contract_amount)}</div>
                    <div className="col-span-2 text-right tabular-nums">
                      <div className="text-emerald-700 font-semibold">{money(r.progress_amount)}</div>
                      <div className="text-[10px] text-emerald-600">{r.progress_pct.toFixed(1)}%</div>
                    </div>
                    <div className="col-span-1 text-right tabular-nums">
                      <div className="text-blue-700 font-semibold">{r.prev_billed_pct.toFixed(1)}%</div>
                      <div className="text-[10px] text-blue-500">{money(r.prev_billed_amount)}</div>
                    </div>
                    <div className="col-span-1 text-right tabular-nums">
                      <div className="text-amber-700 font-semibold">{r.availableEditPct.toFixed(1)}%</div>
                      <div className="text-[10px] text-amber-600">{money(r.contract_amount * r.availableEditPct / 100)}</div>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end gap-1">
                      {!isReadOnly && r.suggested_pct > 0 && (
                        <button
                          data-testid={`prj-invoice-use-progress-${r.scope_id}`}
                          onClick={() => setPcts({ ...pcts, [r.scope_id]: String(r.suggested_pct.toFixed(2)) })}
                          className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          title={`Fill with executed progress not yet billed (${r.suggested_pct.toFixed(1)}%)`}
                        >use</button>
                      )}
                      <div className="relative">
                        <input
                          data-testid={`prj-invoice-pct-${r.scope_id}`}
                          type="text" inputMode="decimal"
                          value={pcts[r.scope_id] ?? ""}
                          disabled={isReadOnly || r.availableEditPct <= 0}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9.]/g, "");
                            setPcts({ ...pcts, [r.scope_id]: v });
                          }}
                          placeholder="0"
                          className={`w-20 rounded border pl-2 pr-5 py-0.5 text-xs text-right tabular-nums outline-none ${r.exceedsCap ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-slate-900"}`}
                        />
                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">%</span>
                      </div>
                    </div>
                    <div className={`col-span-2 text-right tabular-nums font-semibold ${r.exceedsCap ? "text-rose-600" : ""}`}>{money(r.amt)}</div>
                  </div>
                ))}
                {anyOver && (
                  <div className="px-3 py-2 bg-rose-50 border-t border-rose-200 text-[11px] text-rose-700 inline-flex items-center gap-1.5 w-full">
                    <AlertCircle className="w-3.5 h-3.5" /> One or more lines exceed the available %. Draft can still be saved but Approve will fail.
                  </div>
                )}
                <div className="px-3 py-2 bg-emerald-50 border-t border-emerald-100 text-[11px] text-emerald-800 flex items-start gap-1.5">
                  <span className="font-bold shrink-0">Tip:</span>
                  <span>Click <span className="font-bold">use</span> on any line to bill exactly the executed progress that hasn&apos;t been invoiced yet. You can also enter a higher % (bill ahead of execution) — the field is free.</span>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={isReadOnly} rows="2" className={inputCls} /></div>
            <div className="text-right space-y-1">
              <div className="text-[10px] uppercase font-bold text-slate-500">Subtotal</div>
              <div className="text-lg font-semibold tabular-nums">{money(subtotal)}</div>
              <div className="text-[10px] text-slate-500">Tax: <span className="tabular-nums">{money(taxAmount)}</span></div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mt-2">Total</div>
              <div className="text-2xl font-bold tabular-nums text-emerald-700" data-testid="prj-invoice-total">{money(total)}</div>
            </div>
          </div>
        </div>

        {!isReadOnly && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
            <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
            <button
              data-testid="prj-invoice-save-draft"
              onClick={save} disabled={busy}
              className="text-xs px-4 py-1.5 rounded border border-slate-900 hover:bg-slate-50 font-bold disabled:opacity-50 inline-flex items-center gap-1"
            >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Save draft</button>
            <button
              data-testid="prj-invoice-save-approve"
              onClick={saveAndApprove} disabled={busy || anyOver}
              className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1"
            >{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Save & Approve</button>
          </div>
        )}
      </div>
    </div>
  );
}
